import { FormEvent, useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { api, ApiError } from '../api'

const SESSION_EXPIRED_KEY = 'pit_session_expired_notice'

const Login = () => {
  const { isAuthenticated, ready, refresh } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const successMessage = (location.state as { message?: string } | null)?.message ?? null
  const sessionExpiredMessage = useMemo(() => {
    if (typeof window === 'undefined') return null
    try {
      const notice = window.sessionStorage.getItem(SESSION_EXPIRED_KEY)
      if (notice) {
        window.sessionStorage.removeItem(SESSION_EXPIRED_KEY)
        return notice
      }
    } catch {
      // ignore storage read issues
    }
    return null
  }, [])
  const bannerMessage = successMessage ?? sessionExpiredMessage

  const next = (location.state as { from?: string } | null)?.from ?? '/dashboard'

  if (!ready) {
    return (
      <section className="auth-page">
        <p>Preparing login…</p>
      </section>
    )
  }

  if (isAuthenticated) {
    return <Navigate to={next} replace />
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!username.trim() || !password.trim()) {
      setError('Email and password are required.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const response = await api.authLogin({ username: username.trim(), password })
      const token = response?.access_token
      if (!token) {
        throw new Error('Login failed: no token returned.')
      }
      window.localStorage.setItem('pit_jwt', token)
      refresh()
      navigate(next, { replace: true })
    } catch (err) {
      const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Login failed.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="auth-shell auth-login">
      <div className="auth-hero-panel">
        <div className="auth-badge-row">
          <span className="auth-pill">Premium access</span>
          <span className="auth-pill ghost">Privacy links</span>
        </div>
        <h1>Welcome back to Places in Time</h1>
        <p className="lead">
          Continue saved conversations and pick up where you left off. Review our privacy, terms, and legal
          information below before signing in.
        </p>
        <ul className="auth-highlights">
          <li>Resume saved threads</li>
          <li>Adjust tone and knowledge settings from your account</li>
          <li>AI responses may be incomplete or inaccurate</li>
        </ul>
        <div className="auth-legal">
          <Link to="/privacy-policy-gdpr">Privacy & GDPR</Link>
          <span aria-hidden="true">•</span>
          <Link to="/terms-of-use">Terms of Use</Link>
          <span aria-hidden="true">•</span>
          <Link to="/impressum">Impressum</Link>
        </div>
      </div>

      <div className="auth-card" role="form" aria-labelledby="login-heading">
        <div className="auth-card-header">
          <p className="eyebrow">Account access</p>
          <h2 id="login-heading">Log in</h2>
          <p className="muted">Use your credentials to enter your workspace. Successful sign-in returns you to your dashboard.</p>
        </div>

        {bannerMessage && <p className="auth-alert success">{bannerMessage}</p>}
        {error && <p className="auth-alert error">{error}</p>}

        <form className="auth-form" onSubmit={handleSubmit}>
          <label htmlFor="login-username">Email address</label>
          <input
            id="login-username"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
            placeholder="e.g., alexander@places-in-time.com"
          />

          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            placeholder="••••••••"
          />

          <div className="auth-actions">
            <button type="submit" className="button primary" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Log in'}
            </button>
            <p className="form-legal">
              By continuing you confirm you have read our <Link to="/privacy-policy-gdpr">Privacy Policy</Link> and
              <Link to="/terms-of-use"> Terms of Use</Link>.
            </p>
          </div>
        </form>

        <div className="auth-alt">
          <div>
            <p className="muted">Need an account?</p>
            <p className="muted">Registration takes under a minute.</p>
          </div>
          <Link className="button ghost" to="/register" state={{ from: location }}>
            Create account
          </Link>
        </div>
      </div>
    </section>
  )
}

export default Login
